import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Navigate } from 'react-router-dom';

interface Message {
  id: string;
  name: string;
  email: string;
  message: string;
  created_at: string;
  read: boolean;
}

export function Admin() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    checkAdminStatus();
  }, []);

  async function checkAdminStatus() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsAdmin(false);
        return;
      }
      fetchMessages();

      const { data: profile } = await supabase
        .from('users_profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      setIsAdmin(profile?.is_admin || false);
      if (profile?.is_admin) {
        fetchMessages();
      }
    } catch (err) {
      console.error('Error checking admin status:', err);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }

  async function fetchMessages() {
    try {
      const { data, error: fetchError } = await supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setMessages(data || []);
      console.log('Messages fetched:', messages);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages');
    }
  }

  async function toggleRead(id: string, currentRead: boolean) {
    try {
      const { error: updateError } = await supabase
        .from('contact_messages')
        .update({ read: !currentRead })
        .eq('id', id);

      if (updateError) throw updateError;
      
      setMessages(messages.map(msg => 
        msg.id === id ? { ...msg, read: !currentRead } : msg
      ));
    } catch (err) {
      console.error('Error updating message:', err);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-8">
      <h1 className="text-3xl font-bold mb-8">Contact Messages</h1>
      
      <div className="space-y-6">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`p-6 rounded-lg shadow-md ${
              message.read ? 'bg-gray-50' : 'bg-white border-l-4 border-blue-600'
            }`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold">{message.name}</h3>
                <a href={`mailto:${message.email}`} className="text-blue-600 hover:underline">
                  {message.email}
                </a>
              </div>
              <button
                onClick={() => toggleRead(message.id, message.read)}
                className={`px-3 py-1 rounded text-sm ${
                  message.read
                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                {message.read ? 'Mark as Unread' : 'Mark as Read'}
              </button>
            </div>
            
            <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
            
            <div className="mt-4 text-sm text-gray-500">
              {new Date(message.created_at).toLocaleString()}
            </div>
          </div>
        ))}
        
        {messages.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No messages yet
          </div>
        )}
      </div>
    </div>
  );
}